// What's on the plate, as facts read from the recipe (no nutrient numbers needed).
// Goal-aware ranking uses these features; each scoring rule cites a ProductRule.

import type { Recipe } from '../recipes';
import { INGREDIENTS, displayName, type IngredientId } from '../taxonomy';
import type { Goal } from '../preferences';

/** Aromatics and garnishes don't count as a vegetable portion. */
const NOT_A_VEG_PORTION = new Set<IngredientId>(['garlic', 'ginger', 'scallions', 'fresh_basil', 'cilantro', 'lemon', 'lime']);
const WHOLE_GRAINS = new Set<IngredientId>(['brown_rice', 'oats', 'quinoa']);
const STARCHES = new Set<IngredientId>([
  'rice', 'white_rice', 'brown_rice', 'leftover_rice', 'pasta', 'spaghetti', 'bread', 'flour_tortillas',
  'corn_tortillas', 'oats', 'quinoa', 'potatoes', 'sweet_potatoes', 'corn',
]);
const RICH = new Set<IngredientId>(['heavy_cream', 'butter', 'cheese', 'cheddar', 'mozzarella', 'parmesan', 'cream_cheese', 'coconut_milk', 'mayonnaise', 'avocado', 'peanut_butter']);

export interface PlateFeatures {
  /** The main protein food, if the dish is built around one. */
  protein: IngredientId | null;
  /** Grams of that protein food per serving, when the recipe gives grams. */
  proteinGramsPerServing: number | null;
  vegetables: IngredientId[];
  starch: IngredientId | null;
  wholeGrain: boolean;
  /** Has protein, at least one vegetable and a grain or starch. */
  balanced: boolean;
  /** Protein plus starch plus a richer ingredient: a more energy-dense, filling plate. */
  hearty: boolean;
}

export function plateFeatures(recipe: Recipe): PlateFeatures {
  const main = recipe.ingredients.filter((i) => i.role !== 'pantry');
  const proteinIng = recipe.ingredients.find((i) => i.role === 'core' && INGREDIENTS[i.id].category === 'protein');
  const vegetables = [...new Set(main
    .filter((i) => ['vegetable', 'frozen'].includes(INGREDIENTS[i.id].category) && !NOT_A_VEG_PORTION.has(i.id) && !STARCHES.has(i.id) && i.id !== 'frozen_berries')
    .map((i) => i.id))];
  const starchIng = main.find((i) => STARCHES.has(i.id) && i.role === 'core') ?? main.find((i) => STARCHES.has(i.id));
  const rich = recipe.ingredients.some((i) => i.role === 'core' && RICH.has(i.id));
  const protein = proteinIng?.id ?? null;
  return {
    protein,
    proteinGramsPerServing: proteinIng?.unit === 'g' && proteinIng.qty ? Math.round(proteinIng.qty / recipe.servings) : null,
    vegetables,
    starch: starchIng?.id ?? null,
    wholeGrain: recipe.ingredients.some((i) => WHOLE_GRAINS.has(i.id)),
    balanced: !!protein && vegetables.length > 0 && !!starchIng,
    hearty: !!protein && !!starchIng && rich,
  };
}

export interface GoalFit {
  points: number;
  /** Shown to the user only when points > 0. */
  reason: string | null;
  ruleId: string | null;
}

/** How well a dinner suits the user's goals, from plate facts only. */
export function goalFit(recipe: Recipe, goals: Goal[]): GoalFit {
  const f = plateFeatures(recipe);
  const g = new Set(goals);
  const fits: GoalFit[] = [];

  if ((g.has('muscle') || g.has('performance')) && f.protein) {
    fits.push({ points: 14, reason: `Built around ${displayName(f.protein)}, for your ${g.has('muscle') ? 'muscle' : 'training'} goal`, ruleId: 'protein_anchor' });
  }
  if (g.has('gain')) {
    if (f.hearty) fits.push({ points: 12, reason: 'A hearty plate, for your weight-gain goal', ruleId: 'protein_anchor' });
    else if (f.protein && f.starch) fits.push({ points: 8, reason: `${capitalized(displayName(f.protein))} with ${displayName(f.starch)}, a filling combination`, ruleId: 'protein_anchor' });
  }
  if (g.has('lose') && f.vegetables.length >= 2) {
    fits.push({ points: 10, reason: `${f.vegetables.length} vegetables in one meal`, ruleId: 'vegetable_variety' });
  }
  if ((g.has('balanced') || g.has('maintain') || g.has('lose')) && f.balanced) {
    fits.push({ points: 10, reason: 'A balanced plate: protein, vegetables and a grain', ruleId: 'balanced_plate' });
  }
  if (g.has('quality')) {
    if (f.vegetables.length >= 2) fits.push({ points: 8, reason: `${f.vegetables.length} vegetables in one meal`, ruleId: 'vegetable_variety' });
    if (f.wholeGrain) fits.push({ points: 4, reason: 'Made with a whole grain', ruleId: 'balanced_plate' });
  }
  if (!fits.length) return { points: 0, reason: null, ruleId: null };
  const best = fits.sort((a, b) => b.points - a.points)[0];
  // Small bonus for satisfying more than one goal, without double-counting.
  return { ...best, points: best.points + Math.min(6, (fits.length - 1) * 3) };
}

function capitalized(s: string) {
  return s[0].toUpperCase() + s.slice(1);
}
